import { useIntl } from 'react-intl';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  NativeSelect,
  Stack
} from '@mui/material';
import PropTypes from 'prop-types';
import Checkbox from '@mui/material/Checkbox';
import EditNoteIcon from '@mui/icons-material/EditNote';
import Tooltip from '@mui/material/Tooltip';
import ClearIcon from '@mui/icons-material/Clear';
import { useGetAiModelsByProviderIdQuery, useGetAiProvidersQuery } from 'services/aiProviderApi';
import StructuredOutputBuilderDialog from 'pages/assistants/builder/components/sructuredOutputBuilder/structuredOutputBuilderDialog';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';

export function ModelNodeForm({ id, data, onUpdateData }) {
  const { formatMessage: msg } = useIntl();
  const [structuredOutputBuilderDialogOpen, setStructuredOutputBuilderDialogOpen] = useState(false);
  const { providerCode, modelCode } = data;

  // Fetch providers from API
  const { data: providers = [], isLoading: providersLoading } = useGetAiProvidersQuery();

  const { control, setValue, watch } = useForm({
    defaultValues: {
      providerCode: providerCode || '',
      modelCode: modelCode || '',
      isStructuredOutput: data?.isStructuredOutput ?? false,
      schema: data?.schema || {},
      outputVar: data?.outputVar || 'output',
      isStreamed: data?.isStreamed ?? false
    }
  });

  const handleConfigureClick = () => {
    setStructuredOutputBuilderDialogOpen(true);
  };

  const handleSaveStructuredOutput = (schema) => {
    setValue('schema', schema || {});
  };

  const formValues = watch();
  const { isStructuredOutput, isStreamed } = formValues;

  // Si isStreamed passe à vrai et outputVar vaut "output", la remplacer par "noOutput"
  useEffect(() => {
    if (isStreamed && formValues.outputVar === 'output') {
      setValue('outputVar', 'noOutput');
    } else if (!isStreamed && formValues.outputVar === 'noOutput') {
      setValue('outputVar', 'output');
    }
  }, [isStreamed, formValues.outputVar, setValue]);

  const filteredProviders = providers.filter((p) => p.displayableKey !== '***********');
  const selectedProvider =
    filteredProviders.find((p) => p.code === formValues.providerCode) ?? filteredProviders[0];
  const selectedProviderId = selectedProvider?.id;

  const { data: models = [], isLoading: modelsLoading } = useGetAiModelsByProviderIdQuery(
    selectedProviderId,
    { skip: !selectedProviderId }
  );

  // Sort models: favorites first, then alphabetically by label
  const sortedModels = [...models].sort((a, b) => {
    if (a.favorite && !b.favorite) return -1;
    if (!a.favorite && b.favorite) return 1;

    return a.label.localeCompare(b.label);
  });

  // Initialize provider and model when data loads
  useEffect(() => {
    if (filteredProviders.length > 0 && !formValues.providerCode) {
      setValue('providerCode', filteredProviders[0].code);
    }
  }, [filteredProviders, formValues.providerCode, setValue]);

  // Initialize model when data loads
  useEffect(() => {
    if (sortedModels.length > 0 && (!formValues.modelCode || formValues.modelCode === '-1')) {
      setValue('modelCode', sortedModels[0].code);
    }
    if (formValues.providerCode === '-1') {
      setValue('modelCode', '-1');
    }
  }, [sortedModels, formValues.providerCode, formValues.modelCode, setValue]);

  useEffect(() => {
    const updatedData = {
      ...data,
      providerCode: formValues.providerCode,
      modelCode: formValues.modelCode,
      isStructuredOutput: formValues.isStructuredOutput,
      schema: formValues.isStructuredOutput ? formValues.schema : {},
      outputVar: formValues.outputVar,
      isStreamed: formValues.isStreamed
    };

    onUpdateData?.({
      id,
      data: updatedData
    });
  }, [formValues, data, onUpdateData, id]);

  const clearField = (fieldName) => {
    setValue(fieldName, '');
  };

  return (
    <>
      <Stack spacing={1} sx={{ p: 1 }}>
        <Controller
          name="providerCode"
          control={control}
          rules={{ required: msg({ id: 'field.provider.required' }) }}
          render={({ field }) => (
            <FormControl fullWidth>
              <InputLabel variant="standard" htmlFor="provider-native-select">
                {msg({ id: 'field.provider.label' })}
              </InputLabel>
              {providersLoading ? (
                <CircularProgress size={24} />
              ) : (
                <NativeSelect
                  {...field}
                  label={msg({ id: 'field.provider.label' })}
                  required
                  inputProps={{
                    id: 'provider-native-select',
                    name: 'providerCode'
                  }}
                >
                  <option value="-1">Sélectionner un fournisseur</option>
                  {filteredProviders.map((provider) => (
                    <option key={provider.code} value={provider.code}>
                      {provider.label}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </FormControl>
          )}
        />
        <Controller
          name="modelCode"
          control={control}
          rules={{ required: msg({ id: 'field.model.required' }) }}
          render={({ field }) => (
            <FormControl fullWidth>
              <InputLabel variant="standard" htmlFor="model-native-select">
                {msg({ id: 'field.model.label' })}
              </InputLabel>
              {modelsLoading ? (
                <CircularProgress size={24} />
              ) : (
                <NativeSelect
                  {...field}
                  label={msg({ id: 'field.model.label' })}
                  required
                  disabled={!selectedProviderId}
                  inputProps={{
                    id: 'model-native-select',
                    name: 'modelCode'
                  }}
                >
                  <option value="-1">{msg({ id: 'field.model.default.label' })}</option>
                  {sortedModels.map((model) => (
                    <option key={model.code} value={model.code}>
                      {model.label}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </FormControl>
          )}
        />

        <Controller
          name="outputVar"
          control={control}
          render={({ field }) => (
            <MDInput
              {...field}
              label={msg({ id: 'field.outputvar.label' })}
              variant="filled"
              fullWidth
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={msg({ id: 'tooltip.clear' })}>
                      <IconButton onClick={() => clearField('outputVar')}>
                        <ClearIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
            />
          )}
        />
        <Controller
          name="isStreamed"
          control={control}
          render={({ field: { onChange, value, ...field } }) => (
            <FormControlLabel
              control={<Checkbox onChange={onChange} checked={value} {...field} />}
              label={msg({ id: 'field.streamed.label' })}
            />
          )}
        />

        <Controller
          name="isStructuredOutput"
          control={control}
          render={({ field: { onChange, value, ...field } }) => (
            <FormControlLabel
              control={<Checkbox onChange={onChange} checked={value} {...field} />}
              label={msg({ id: 'field.structuredOutput.label' })}
            />
          )}
        />

        {isStructuredOutput && (
          <MDButton
            id="configureBtn"
            size="small"
            color="primary"
            variant="gradient"
            startIcon={<EditNoteIcon />}
            autoFocus
            onClick={handleConfigureClick}
            sx={{ mt: 1 }}
          >
            {msg({ id: 'action.configure' })}
          </MDButton>
        )}
      </Stack>

      <StructuredOutputBuilderDialog
        dialogOpen={structuredOutputBuilderDialogOpen}
        setDialogOpen={setStructuredOutputBuilderDialogOpen}
        initialSchema={formValues.schema}
        onSave={handleSaveStructuredOutput}
      />
    </>
  );
}

ModelNodeForm.propTypes = {
  id: PropTypes.string,
  data: PropTypes.shape({
    id: PropTypes.number,
    providerCode: PropTypes.string,
    modelCode: PropTypes.string,
    isStructuredOutput: PropTypes.bool,
    schema: PropTypes.object,
    outputVar: PropTypes.string,
    isStreamed: PropTypes.bool
  }),
  onUpdateData: PropTypes.func.isRequired
};
